<template>
  <div
    class="inlineMediaTray"
    v-if="items && items.length"
    role="list"
    aria-label="Media thumbnails"
  >
    <div class="thumbsStrip" @dragover.prevent>
      <div
        v-for="(it, i) in items"
        :key="it.key ?? it.originalIndex ?? i"
        class="thumb"
        :class="{
          selected: i === selectedIndex,
          isThumbnail: isThumb(i),
          hover: hoverIndex === i,
          dragging: touchDragging && draggingIndex === i,
        }"
        role="listitem"
        :aria-selected="i === selectedIndex ? 'true' : 'false'"
        tabindex="0"
        @click="$emit('select', i)"
        @keydown="onThumbKeydown(i, $event)"
        :ref="(el) => (thumbRefs[i] = el)"
        :draggable="!isTouch"
        @dragstart="onDragStart(i, $event)"
        @dragenter.prevent="onDragEnter(i)"
        @dragover.prevent
        @drop.prevent="onDrop(i)"
        @touchstart.passive="onTouchStart(i, $event)"
        @touchmove="onTouchMove($event)"
        @touchend="onTouchEnd"
      >
        <template v-if="isVideo(it)">
          <img
            v-if="videoThumbSrc(it)"
            class="thumbImg"
            :src="videoThumbSrc(it)"
            alt=""
          />
          <video
            v-else
            class="thumbImg"
            :src="it.src"
            muted
            playsinline
            preload="metadata"
          ></video>
        </template>
        <template v-else-if="isDoc(it)">
          <div class="thumbFile">
            <small class="fileName" :title="it.name || 'File'">{{
              it.name || "File"
            }}</small>
          </div>
        </template>
        <template v-else>
          <img class="thumbImg" :src="it.src" alt="" />
        </template>
        <!-- Badges row: keep all badges in the same row container -->
        <div class="badgesRow">
          <div v-if="isVideo(it)" class="badge badgeVideo">Video</div>
          <div v-else-if="isDoc(it)" class="badge badgeFile">Doc</div>
          <div v-if="isThumb(i)" class="badge badgeThumb">Thumbnail</div>
        </div>
        <div class="thumbActions" @click.stop>
          <svg
            class="smallEdit compactImg thumbBtn"
            v-haptic
            role="button"
            tabindex="0"
            aria-label="Edit image"
            v-if="!isDoc(it) && !hideEdit"
            @click="$emit('edit', i)"
            @keydown.enter.prevent="$emit('edit', i)"
            @keydown.space.prevent="$emit('edit', i)"
          >
            <use href="@/assets/icons/iconset.svg#edit_compact"></use>
          </svg>
          <svg
            class="smallClose compactImg thumbBtn"
            v-haptic
            role="button"
            tabindex="0"
            aria-label="Remove image"
            @click="$emit('remove', i)"
            @keydown.enter.prevent="$emit('remove', i)"
            @keydown.space.prevent="$emit('remove', i)"
          >
            <use href="@/assets/icons/iconset.svg#trash_compact"></use>
          </svg>
        </div>
      </div>
      <!-- Slot for appending extra content (e.g., upload placeholder) -->
      <slot name="append"></slot>
    </div>
  </div>
  <div v-else class="inlineMediaTray empty" aria-hidden="true">
    <!-- Allow slot even when empty -->
    <div class="thumbsStrip">
      <slot name="append"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: "InlineMediaTray",
  props: {
    items: { type: Array, default: () => [] }, // [{ src, type?: 'image'|'video'|'file', name?, originalIndex? }]
    selectedIndex: { type: Number, default: 0 },
    // When true: index 0 is the designated thumbnail slot and gets the outline.
    // When false: the selectedIndex is considered the thumbnail (used in PostComposer).
    thumbnailFirst: { type: Boolean, default: true },
    // When true: hide the edit (pencil) icon on thumbnails
    hideEdit: { type: Boolean, default: false },
  },
  emits: ["select", "remove", "edit", "reorder"],
  data() {
    return {
      thumbRefs: [],
      draggingIndex: null,
      isTouch: false,
      touchTimer: null,
      touchDragging: false,
      hoverIndex: null,
      touchStartX: 0,
      touchStartY: 0,
    };
  },
  watch: {
    selectedIndex(newVal) {
      this.$nextTick(() => {
        const el = this.thumbRefs[newVal];
        if (el && typeof el.focus === "function") {
          el.focus();
        }
      });
    },
  },
  mounted() {
    try {
      this.isTouch =
        typeof window !== "undefined" &&
        ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    } catch {
      this.isTouch = false;
    }
    // ensure thumbRefs length matches items to avoid undefined access during touch moves
    this.$nextTick(() => {
      this.thumbRefs = this.thumbRefs || [];
      for (let i = 0; i < (this.items?.length || 0); i++)
        if (!this.thumbRefs[i]) this.thumbRefs[i] = null;
    });
  },
  methods: {
    isVideo(it) {
      const t = (it?.type || "").toLowerCase();
      const src = String(it?.src || it?.url || it?.name || "");
      return t === "video" || /\.(mp4|webm|mov|m4v)(\?|$)/i.test(src);
    },
    isDoc(it) {
      const t = (it?.type || "").toLowerCase();
      if (t && t !== "image" && t !== "video") return true; // generic file
      const src = String(it?.src || it?.url || it?.name || "");
      return (
        /\.(pdf|docx?|pptx?|xlsx?|txt)(\?|$)/i.test(src) || it?.type === "file"
      );
    },
    videoThumbSrc(it) {
      return it?.poster || "";
    },
    isThumb(i) {
      return this.thumbnailFirst ? i === 0 : i === this.selectedIndex;
    },
    onDragStart(i, e) {
      this.draggingIndex = i;
      try {
        e.dataTransfer.setData("text/plain", String(i));
      } catch {}
      e.dataTransfer.effectAllowed = "move";
    },
    // Touch long-press handlers for mobile reorder & to allow native scrolling
    onTouchStart(i, e) {
      if (!this.isTouch) return;
      const t = e.touches && e.touches[0];
      this.touchStartX = t?.clientX || 0;
      this.touchStartY = t?.clientY || 0;
      this.touchTimer = setTimeout(() => {
        this.touchDragging = true;
        this.draggingIndex = i;
        this.hoverIndex = i;
        // Lock horizontal scroll on the strip while dragging
        const strip = this.$el?.querySelector('.thumbsStrip');
        if (strip) strip.style.overflowX = 'hidden';
        // Haptic feedback on drag activation
        if ('vibrate' in navigator) navigator.vibrate(50);
      }, 500);
    },
    onTouchMove(e) {
      if (!this.isTouch) return;
      const t = e.touches && e.touches[0];
      if (!t) return;
      const dx = Math.abs(t.clientX - this.touchStartX);
      const dy = Math.abs(t.clientY - this.touchStartY);
      if (!this.touchDragging && (dx > 8 || dy > 8)) {
        clearTimeout(this.touchTimer);
        this.touchTimer = null;
        return; // allow native scroll
      }
      if (this.touchDragging) {
        e.preventDefault();
        e.stopPropagation();
        const x = t.clientX;
        let best = this.draggingIndex;
        let bestDist = Infinity;
        for (let idx = 0; idx < this.thumbRefs.length; idx++) {
          const el = this.thumbRefs[idx];
          if (!el) continue;
          const r = el.getBoundingClientRect();
          const cx = r.left + r.width / 2;
          const dist = Math.abs(cx - x);
          if (dist < bestDist) {
            bestDist = dist;
            best = idx;
          }
        }
        if (best !== this.hoverIndex) this.hoverIndex = best;
      }
    },
    onTouchEnd() {
      if (!this.isTouch) return;
      clearTimeout(this.touchTimer);
      this.touchTimer = null;
      if (this.touchDragging) {
        const from = this.draggingIndex;
        const to = this.hoverIndex != null ? this.hoverIndex : from;
        this.touchDragging = false;
        this.draggingIndex = null;
        this.hoverIndex = null;
        // Restore horizontal scroll
        const strip = this.$el?.querySelector('.thumbsStrip');
        if (strip) strip.style.overflowX = '';
        if (from != null && to != null && from !== to) {
          this.$emit("reorder", { from, to });
        }
      }
    },
    onDragEnter(i) {
      // Optional: could add visual cue
    },
    onDrop(i) {
      const from = this.draggingIndex;
      const to = i;
      this.draggingIndex = null;
      if (from === null || from === undefined || from === to) return;
      this.$emit("reorder", { from, to });
    },
    onThumbKeydown(i, e) {
      const last = (this.items?.length || 1) - 1;
      const curr = this.items?.[i];
      switch (e.key) {
        case "ArrowRight":
          e.preventDefault();
          this.$emit("select", Math.min(i + 1, last));
          break;
        case "ArrowLeft":
          e.preventDefault();
          this.$emit("select", Math.max(i - 1, 0));
          break;
        case "Home":
          e.preventDefault();
          this.$emit("select", 0);
          break;
        case "End":
          e.preventDefault();
          this.$emit("select", last);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          this.$emit("select", i);
          break;
        case "Backspace":
        case "Delete":
          e.preventDefault();
          this.$emit("remove", i);
          break;
        default:
          if ((e.key === "e" || e.key === "E") && !this.isDoc(curr)) {
            e.preventDefault();
            this.$emit("edit", i);
          }
      }
    },
  },
};
</script>

<style scoped>
.inlineMediaTray {
  width: 100%;
  padding: var(--space-4);
  box-shadow: var(--shadow-surface-recessed);
  border-radius: var(--radius-lg);
  background: var(--color-surface-raised);
}
.thumbsStrip {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: calc((var(--control-height-default, 48px)) * 2);
  gap: var(--space-3);
  align-items: center;
  overflow-x: auto;
}
.thumb {
  position: relative;
  width: calc((var(--control-height-default, 48px)) * 2);
  height: calc((var(--control-height-default, 48px)) * 2);
  border-radius: var(--radius-md);
  background: var(--color-surface-raised);
  overflow: hidden;
  cursor: pointer;
}
.thumb.selected {
  outline: 2px solid var(--color-accent-primary);
}
.thumb.isThumbnail {
  border: 2px solid var(--color-accent-emphasis);
}
.thumb.dragging {
  transform: scale(1.12);
  z-index: 10;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  opacity: 0.85;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.thumb.hover:not(.dragging) {
  outline: 2px dashed var(--color-accent-primary, #5856d6);
  outline-offset: 2px;
}
.thumbImg {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.thumbFile {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  color: var(--color-text-primary);
  background: var(--color-accent-surface-muted);
  text-align: center;
  padding: 6px;
}
.fileName {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.thumbActions {
  position: absolute;
  top: var(--space-2);
  right: var(--space-2);
  display: flex;
  gap: var(--space-1, 4px);
}
svg.smallClose {
  position: initial;
}
.empty {
  min-height: 8px;
}
.empty .thumbsStrip {
  display: flex;
  gap: var(--space-3);
}
.thumbBtn {
  background: rgba(0, 0, 0, 0.4);
  color: var(--color-text-on-media);
  border-radius: calc(var(--radius-sm, 8px));
  padding: var(--space-1, 4px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  /* Blur removed; keep subtle saturation if needed */
  /* backdrop-filter: saturate(180%); */
}
.badge {
  position: relative;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 999px;
  color: #fff;
}
.badgesRow {
  position: absolute;
  left: 6px;
  bottom: 6px;
  display: flex;
  gap: 6px;
  align-items: center;
}
.badgeVideo {
  background: rgba(220, 0, 0, 0.8);
}
.badgeFile {
  background: rgba(0, 0, 0, 0.6);
}
.badgeThumb {
  background: var(--color-accent-primary, rgba(88, 86, 214, 0.85));
}
.iconSm {
  width: 20px;
  height: 20px;
  fill: var(--color-icon-supporting);
}
</style>
